import { FC, createRef, useEffect, useState } from 'react';

import {
  ArrowUpOutlined,
  EnvironmentOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import { Chart } from 'chart.js';
import * as ChartJs from 'chart.js';
import CountUp from 'react-countup';
import { T } from 'react-translator-component';

import { COLORS } from '../../../../utils/theme/colors';
import Card from '../../Card';
import * as S from '../styled';

ChartJs.Chart.register.apply(
  null,
  Object.values(ChartJs).filter((chartClass) => (chartClass as any).id) as any,
);

export type StationIndicatorProps = {
  total: number;
  inUse: number;
  available: number;
  unavailable: number;
  maintenance: number;
};

const StationIndicator: FC<StationIndicatorProps> = ({
  total = 0,
  inUse = 0,
  available = 0,
  unavailable = 0,
  maintenance = 0,
}) => {
  const [chart, setChart] = useState({});
  const [initializedChart, setInitializedChart] = useState(false);
  const chartRef = createRef();

  function removeData(chart) {
    chart.data.datasets.forEach((dataset) => {
      delete dataset.data;
    });
    chart.update();
  }

  function addDataset(chart, data) {
    chart.data.datasets.forEach((dataset) => {
      dataset.data = data;
    });
    chart.update();
  }

  useEffect(() => {
    const data = {
      labels: [
        T('Available'),
        T('Unavailable'),
        T('Charging'),
        T('Maintenance'),
      ],
      datasets: [
        {
          label: 'Dataset 1',
          data: [available, unavailable, inUse, maintenance],
          backgroundColor: [
            COLORS.available,
            COLORS.unavailable,
            COLORS.busy,
            COLORS.maintenance,
          ],
        },
      ],
      scale: {
        tick: '1px',
      },
    };
    const config = {
      type: 'pie',
      data: data,
      options: {
        elements: {
          arc: {
            borderWidth: 0,
          },
        },
        cutout: '80%',
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: COLORS.stone,
            color: '#000',
            labelColor: '#000',
            labelTextColor: '#000',
            bodyColor: '#000',
            bodyFont: {
              size: 11,
              family: 'Roboto',
            },
            xAlign: 'center',
            yAlign: 'top',
            padding: 4,
            callbacks: {
              labelColor: function (tooltipItem) {
                return {
                  backgroundColor:
                    tooltipItem.dataset.backgroundColor[tooltipItem.dataIndex],
                  borderColor: COLORS.stone,
                };
              },
              label: (item) => {
                return `${item.label[0]}: ${item.parsed.toFixed(1)}%`;
              },
            },
          },
        },
      },
    };
    if (!initializedChart) {
      const myChartRef = (chartRef as any).current.getContext('2d');
      if (
        available !== 0 ||
        inUse !== 0 ||
        unavailable !== 0 ||
        maintenance !== 0
      ) {
        setChart(new Chart(myChartRef, config as any));

        setInitializedChart(true);
      }
    } else {
      removeData(chart);
      addDataset(chart, [available, unavailable, inUse, maintenance]);
    }
  }, [
    available,
    inUse,
    unavailable,
    chartRef,
    chart,
    maintenance,
    initializedChart,
  ]);

  return (
    <Card
      title={T('Chargeboxes')}
      indicators={[
        {
          values: (
            <div className='description_registered_wrapper'>
              <EnvironmentOutlined
                className='indicators--icon'
                style={{ color: '#EA4235' }}
              />
              <S.WrapperStationIcon>
                <CountUp start={0} end={total} duration={3} />
              </S.WrapperStationIcon>
            </div>
          ),
          labels: (
            <div className='description_registered_wrapper'>
              <ArrowUpOutlined style={{ color: '#EA4230' }} />
              <p className='description_registered'>Total de estações</p>
            </div>
          ),
        },
        {
          values: (
            <S.Div>
              <S.Wrapper>
                <div className='canvas'>
                  <canvas ref={chartRef as any} />
                </div>
                <div className='indicators--pie'>
                  <div className='indicators--pie-text'>
                    <CountUp
                      className='number_registered--text'
                      start={0}
                      end={inUse}
                      duration={3}
                    />
                    <PercentageOutlined
                      className='number_registered--text'
                      style={{ color: '#333' }}
                    />
                  </div>
                </div>
              </S.Wrapper>
            </S.Div>
          ),
          labels: (
            <div className='description_registered_wrapper'>
              <ArrowUpOutlined style={{ color: COLORS.busy }} />
              <p className='description_registered'>Total de ocupadas</p>
            </div>
          ),
        },
      ]}
    />
  );
};

export default StationIndicator;
